import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { CustomerService } from '../../../services/customer.service';
import { ClientType, CustomerCrudAppliedFilter, CustomerCrudFilter, CustomerCrudTable, ServiceType, TypeOfConnection } from '../../../models/customer.model';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { ChecklistItem } from '../../../models/checklist-item.model';
import { UtilitySharedService } from '../../../shared/_services/utility-shared.service';
import {PermissionsService} from 'shared-lib';

enum SearchType {
  AccountNumber = 0,
  MeterNmber = 1,
  SLRN = 2,
  BuildingOwner = 3,
  Address = 4
}

@Component({
  selector: 'app-crud-customer-accounts',
  templateUrl: './crud-customer-accounts.component.html',
  styleUrl: './crud-customer-accounts.component.scss',
  standalone: false
})
export class CrudCustomerAccountsComponent implements OnInit {

  currentPage: number = 1;
  count: number = 0;
  pageSize: number = 10;
  customers: CustomerCrudTable[] = [];
  utilityId: number = 0;
  searchText: string = "";
  isNewConnection: boolean = false;
  selectedCustomerId: number = 0;
  isFilterWindowOpened: boolean = false;
  checklistItems: ChecklistItem[] = [];
  appliedFilters: CustomerCrudAppliedFilter[] = [];
  currentFilter: CustomerCrudFilter = new CustomerCrudFilter();
  searchDropdownOpened: boolean = false;
  searchByNameOutput: string = 'COMMON.CUSTOMER.ACCOUNT_NUMBER';
  searchByValue: SearchType = SearchType.AccountNumber;
  searchTypeOption = SearchType;

  clientTypes = [
    { label: 'COMMON.CLIENT_TYPE_OPTIONS.COMPANY', value: ClientType.Company },
    { label: 'COMMON.CLIENT_TYPE_OPTIONS.EMBASSY_FOREIGN_INSTITUTION', value: ClientType.EmbassyForeignInstitution },
    { label: 'COMMON.CLIENT_TYPE_OPTIONS.INDIVIDUAL', value: ClientType.Individual },
    { label: 'COMMON.CLIENT_TYPE_OPTIONS.GOVERNMENT', value: ClientType.Government },
    { label: 'COMMON.CLIENT_TYPE_OPTIONS.ASSEMBLY', value: ClientType.Assembly },
  ];

  serviceTypes = [
    { label: 'COMMON.SERVICE_TYPE.LARGE', value: ServiceType.Large },
    { label: 'COMMON.SERVICE_TYPE.SMALL', value: ServiceType.Small },
    { label: 'COMMON.SERVICE_TYPE.PREPAID', value: ServiceType.Prepaid },
    { label: 'COMMON.SERVICE_TYPE.AMR', value: ServiceType.AMR }
  ];

  statusOptions = [
    { label: 'COMMON.PENDING_SETUP', value: 0 },
    { label: 'COMMON.ASSET_STATUS.COMPLETE', value: 1 }
  ];

  phaseOptions = [
    { label: 'COMMON.CUSTOMER.PHASE_OPTIONS.SINGLE', value: TypeOfConnection.SinglePhase },
    { label: 'COMMON.CUSTOMER.PHASE_OPTIONS.THREE', value: TypeOfConnection.ThreePhase }
  ];

  mapSearchBy: Record<number, string> = {
    [SearchType.AccountNumber]: 'AccountNumber',
    [SearchType.MeterNmber]: 'MeterNumber',
    [SearchType.SLRN]: 'Slrn',
    [SearchType.BuildingOwner]: 'BuildingOwner',
    [SearchType.Address]: 'Address',
  };

  mapSearchByOutput: Record<number, string> = {
    [SearchType.AccountNumber]: 'CUSTOMERS.SEARCH_BY.ACCOUNT_NO',
    [SearchType.MeterNmber]: 'CUSTOMERS.SEARCH_BY.METER_NO',
    [SearchType.SLRN]: 'CUSTOMERS.SEARCH_BY.SLRN',
    [SearchType.BuildingOwner]: 'CUSTOMERS.SEARCH_BY.BUILDING_OWNER',
    [SearchType.Address]: 'COMMON.ADDRESS',
  };

  constructor(
    private customerService: CustomerService,
    private toastr: ToastrService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private utilitySharedService: UtilitySharedService,
    public permissionsService: PermissionsService
  ) {
    this.utilityId = Number(this.utilitySharedService.getUtilityId());
  }

  @Output() filterWindow = new EventEmitter<boolean>();
  @ViewChild('filterTemplate', { static: true })
  filterTemplate!: TemplateRef<any>;

  @Output() filterTemplateReady = new EventEmitter<TemplateRef<any>>();

  @Input() set refreshTrigger(_: number) {
    this.filterTemplateReady.emit(this.filterTemplate);
  }

  ngOnInit(): void {
    this.filterTemplateReady.emit(this.filterTemplate);
    this.loadSavedFilters();
    this.reloadTable(this.currentPage);
  }

  toggleSearchDropdown(){
    this.searchDropdownOpened = !this.searchDropdownOpened;
  }

  selectSearchByOption(searchType: SearchType){
    this.searchByValue = searchType;
    this.searchByNameOutput = this.mapSearchByOutput[searchType];
    this.searchDropdownOpened = false;
  }

  onAddFilterCards(filterCards: CustomerCrudAppliedFilter[]) {
    const existingSearchFilter = this.appliedFilters.find(f => f.label === 'search');
    const nonSearchFilters = this.appliedFilters.filter(f => f.label !== 'search');

    const newFilters = filterCards.filter(
      newFilter => !nonSearchFilters.some(
        existing => existing.label === newFilter.label && existing.value === newFilter.value
      )
    );

    this.appliedFilters = existingSearchFilter
      ? [...nonSearchFilters, ...newFilters, existingSearchFilter]
      : [...nonSearchFilters, ...newFilters];

    this.checklistItems = this.appliedFilters.map(filter => ({
      property: filter.label || '',
      selectedValue: filter.value || '',
      propertyTranslation: filter.label || '',
      valueTranslation: filter.value || ''
    }));

    this.cdr.detectChanges();
  }

  onAddFilter(filter: CustomerCrudFilter) {
    this.currentFilter = { ...this.currentFilter, ...filter };
    // this.reloadTable(1);
  }

  resetFilter() {
    this.appliedFilters = [];
    this.checklistItems = [];
    this.currentFilter = new CustomerCrudFilter();
    this.searchText = '';
    this.isNewConnection = false;
    this.reloadTable(1);
  }

  removeFilter(checklistItem: ChecklistItem) {
    this.appliedFilters = this.appliedFilters.filter(
      (f) => !(f.label === checklistItem.property && f.value === checklistItem.selectedValue)
    );

    this.checklistItems = this.checklistItems.filter(
      (item) => !(item.property === checklistItem.property && item.selectedValue === checklistItem.selectedValue)
    );
    
    if (checklistItem.property === 'search') {
      this.searchText = '';
      this.onSearchChange();
    }
    
    this.currentFilter = new CustomerCrudFilter();
    this.appliedFilters.forEach(filter => {
      switch (filter.label) {
        case 'COMMON.UT.SINGLE':
          this.currentFilter.selectedAreas = [];
          break;
        case 'COMMON.TARIFF.SINGLE':
          this.currentFilter.tariffId = filter.value ? parseInt(filter.value) || 0 : 0;
          break;
        case 'COMMON.MBC_DETAILS.CLIENT_TYPE':
          const clientType = this.clientTypes.find(type => type.label === filter.value);
          this.currentFilter.clientType = clientType ? clientType.value : 0;
          break;
        case 'COMMON.SERVICE_TYPE.TITLE':
          const serviceType = this.serviceTypes.find(type => type.label === filter.value);
          this.currentFilter.serviceType = serviceType ? serviceType.value : 0;
          break;
        case 'CRUD.SLT_STATUS':
          this.currentFilter.sltStatus = isNaN(Number(filter.value)) ? (filter.id !== undefined ? filter.id : null) : Number(filter.id !== undefined ? filter.id : null);
          break;
        case 'COMMON.VALIDATION_STATUS':
          const status = this.statusOptions.find(option => option.label === filter.value);
          this.currentFilter.customerStatus = status ? status.value : 0;
          break;
        case 'COMMON.CUSTOMER.CUSTOMER_PHASE':
          const phase = this.phaseOptions.find(option => option.label === filter.value);
          this.currentFilter.customerPhase = phase ? phase.value : 0;
          break;
      }
    });

    if(this.appliedFilters.length == 0){
      this.reloadTable(1);
    }
  }


  applyFilter() {
    this.reloadTable(1);
  }

  openFilterWindow() {
    this.isFilterWindowOpened = true;
    this.cdr.detectChanges();
  }

  closeFilterWindow() {
    this.isFilterWindowOpened = false;
  }

  setNewConnection() {
    this.isNewConnection = !this.isNewConnection;
    this.reloadTable();
  }

  onSearchChange() {
    if (this.searchText == '') {
      this.onSearch();
    }
  }

  deleteCustomer() {
    this.customerService.delete(this.selectedCustomerId).subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.toastr.success(responseData?.message);
          this.reloadTable(1);
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occurred');
      },
    });
  }

  download() {
    var obj = this.getFilterObject();
    this.customerService.downloadCrud(obj).subscribe({
      next: (response) => {
        const data = response.body;
        if (data?.status === 'OK' || data?.status === '200') {
          window.location.href = data.data;
        } else if (data?.status === 'Pending' || data?.status === '600') {
          this.toastr.info(data?.message || '');
        } else this.toastr.error(data?.message || 'Request failed');
      },
      error: (error) => this.toastr.error(error),
    });
  }

  onSearch() {
    if (this.searchText.trim()) {
      this.checklistItems = this.checklistItems.filter(item => item.property !== 'search');
      this.checklistItems.push({
        property: 'search',
        selectedValue: this.searchText,
        propertyTranslation: 'COMMON.SEARCH',
        valueTranslation: this.searchText
      });
      this.appliedFilters = this.appliedFilters.filter(filter => filter.label !== 'search');
      this.appliedFilters.push({
        label: 'search',
        value: this.searchText
      });
      this.cdr.detectChanges();
    } else {
      this.checklistItems = this.checklistItems.filter(item => item.property !== 'search');
      this.appliedFilters = this.appliedFilters.filter(filter => filter.label !== 'search');
      this.cdr.detectChanges();
    }
    // this.reloadTable(1);
  }

  async reloadTable(page: any = null) {
    if (page) {
      this.currentPage = page;
    }

    let dataIn = {
      pageInfo: {
        page: this.currentPage,
        pageSize: this.pageSize,
        count: this.count,
      },
      filterParams: this.getFilterObject(),
      utilityId: this.utilityId,
    };
    await this.getAllCustomers(dataIn);
  }

  getFilterObject() {
    return {
      search: this.appliedFilters.find(f => f.label === 'search')?.value || null,
      isNewConnection: this.isNewConnection,
      tariffId: this.currentFilter.tariffId !== null && this.currentFilter.tariffId !== undefined ? this.currentFilter.tariffId : null,
      serviceType: this.currentFilter.serviceType !== null && this.currentFilter.serviceType !== undefined ? this.currentFilter.serviceType : null,
      customerStatus: this.currentFilter.customerStatus !== null && this.currentFilter.customerStatus !== undefined ? this.currentFilter.customerStatus : null,
      clientType: this.currentFilter.clientType !== null && this.currentFilter.clientType !== undefined ? this.currentFilter.clientType : null,
      sltStatus: this.currentFilter.sltStatus || null,
      customerPhase: this.currentFilter.customerPhase !== null && this.currentFilter.customerPhase !== undefined ? this.currentFilter.customerPhase : null,
      selectedAreas: this.currentFilter.selectedAreas?.length > 0 ? this.currentFilter.selectedAreas : null,
      selectedRegions: this.currentFilter.selectedRegions?.length > 0 ? this.currentFilter.selectedRegions : null,
      searchType: this.mapSearchBy[this.searchByValue] ?? 'AccountNumber'
    };
  }

  private async getAllCustomers(dataIn: any) {
    try {
      const response = await lastValueFrom(this.customerService.getAllCrud(dataIn));
      const responseData = response.body;
      if (responseData?.status === 200 || responseData?.status === 'OK') {
        this.customers = responseData.data.data;
        this.count = responseData.data.count ?? this.count;
      } else {
        this.toastr.error(responseData?.message);
      }
    } catch (err) {
      this.toastr.error('Error occurred');
    }
  }

  selectCustomer(customerId: number) {
    this.onEditCustomer(customerId);
  }

  onPageChange(newPage: number) {
    this.currentPage = newPage;
    this.reloadTable(newPage);
  }

  onEditCustomer(id: number) {
    this.saveCurrentFilters();
    this.router.navigate(['customers', 'crud-edit', id]);
  }

  private saveCurrentFilters() {
    const filterState = {
      filterParams: this.getFilterObject(),
      appliedFilters: this.appliedFilters,
      checklistItems: this.checklistItems,
      currentPage: this.currentPage,
      count: this.count
    };
    localStorage.setItem('customerFilters', JSON.stringify(filterState));
  }

  private loadSavedFilters() {
    const savedFilters = localStorage.getItem('customerFilters');
    if (savedFilters) {
      try {
        const filterState = JSON.parse(savedFilters);
        if (filterState.filterParams) {
          this.searchText = filterState.filterParams.search || '';
          this.isNewConnection = filterState.filterParams.isNewConnection || false;
          this.currentFilter = new CustomerCrudFilter();
          this.currentFilter.tariffId = filterState.filterParams.tariffId || null;
          this.currentFilter.serviceType = filterState.filterParams.serviceType || null;
          this.currentFilter.customerStatus = filterState.filterParams.customerStatus || null;
          this.currentFilter.clientType = filterState.filterParams.clientType || null;
          this.currentFilter.sltStatus = filterState.filterParams.sltStatus || null;
          this.currentFilter.customerPhase = filterState.filterParams.customerPhase || null;
          this.currentFilter.selectedAreas = filterState.filterParams.selectedAreas || [];
          this.currentFilter.selectedRegions = filterState.filterParams.selectedRegions || [];
        }
        this.appliedFilters = filterState.appliedFilters || [];
        this.checklistItems = filterState.checklistItems || [];
        this.currentPage = filterState.currentPage || 1;
        this.count = filterState.count || 0;
        localStorage.removeItem('customerFilters');
      } catch (error) {
        localStorage.removeItem('customerFilters');
      }
    }
  }
}